import { getClientBuildMeta } from "@pathwright/utils/src/client-build/clientBuildMeta"
import { toaster } from "@pathwright/ux/src/components/chakra/ui/toaster"
import useTranslate from "@pathwright/ux/src/components/lng/useTranslate"
import { useEffect, useState } from "react"
import {
  DeployRefreshConfirmation,
  deployRefreshToastOptions
} from "./deployRefreshPresentation"
import { useLastHeartbeatVar } from "./heartbeat"
import { shouldPromptForDeployRefresh } from "./refreshPrompt"

function reloadConfirmedUpdate() {
  window.location.reload()
}

export function DeployRefreshPrompt() {
  const { t } = useTranslate()
  const { deploy } = useLastHeartbeatVar()
  const [promptOpen, setPromptOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [lastNotifiedDeployId, setLastNotifiedDeployId] = useState<string | null>(
    null
  )

  const runningBuildId = getClientBuildMeta().buildId
  const deployId = deploy?.id ? String(deploy.id) : null
  const isCurrentBuild = !!runningBuildId && deploy?.build_id === runningBuildId
  const isEligible = shouldPromptForDeployRefresh({ runningBuildId, deploy })

  useEffect(() => {
    if (isCurrentBuild) {
      // A rollback can make this tab current without a reload.
      setPromptOpen(false)
      setConfirmOpen(false)
      return
    }

    if (isEligible && deployId && deployId !== lastNotifiedDeployId) {
      // An open prompt already covers this notification; don't repeat it after dismissal.
      setLastNotifiedDeployId(deployId)
      if (!confirmOpen) setPromptOpen(true)
    }
    // A quiet deployment or missing metadata does not withdraw an existing prompt.
  }, [isCurrentBuild, isEligible, deployId, lastNotifiedDeployId, confirmOpen])

  useEffect(() => {
    if (!promptOpen) return
    let disposed = false
    const toastId = toaster.create({
      ...deployRefreshToastOptions,
      title: t("An updated version of Pathwright is available"),
      icon: "rocket",
      action: {
        label: t("Update"),
        onClick: () => setConfirmOpen(true)
      },
      onStatusChange: ({ status }) => {
        // The action also dismisses the toast; its confirmation remains open.
        if (status === "dismissing" && !disposed) setPromptOpen(false)
      }
    })

    return () => {
      disposed = true
      toaster.dismiss(toastId)
    }
  }, [promptOpen, t])

  return (
    <DeployRefreshConfirmation
      open={confirmOpen && !isCurrentBuild}
      onClose={() => setConfirmOpen(false)}
      onRefresh={reloadConfirmedUpdate}
    />
  )
}
